import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { RootContainerCfgUIElement } from '@/elements/containers/RootContainer/RootContainer';
import { MixedContainerCfgUIElement } from '@/elements/containers/MixedContainer/MixedContainer';
import { ComponentItemsContainerCfgUIElement } from '@/elements/containers/ComponentItemsContainer/ComponentItemsContainer';
import { ProductsContainerCfgUIElement } from '@/elements/containers/ProductsContainer/ProductsContainer';
import { ElementRenderer } from '@/elements/ElementRenderer';
import {
  createMockTextAttributeChild,
  createMockBooleanAttributeChild,
  createMockIntegerAttributeChild,
  createMockComponentChild,
  createMockContainerChild,
  MockCompositeProductInfoState,
} from '../mocks';
import {
  mockRootContainer,
  mockMixedContainer,
  mockMixedContainerGrid,
  mockComponentItemsContainer,
  mockProductMergeContainer,
  mockColorAttributeConfig,
  mockSizeAttributeConfig,
  mockCoffeeMachineComponent,
  // Container title variations
  mockContainerTitleDesc,
  mockContainerTitleOnly,
  mockContainerNoTitle,
  // Container state variations
  mockContainerExpandedByDefault,
  mockContainerCollapsedByDefault,
  mockContainerNonExpandable,
  // Nested container variations
  mockNestedMixedLevel2,
  mockNestedMixedLevel3,
  mockNestedComponentItems,
  // Rich content configs for comprehensive demos
  mockRichColorAttribute,
  mockRichSizeAttribute,
  mockRichBooleanAttribute,
  mockRichIntegerAttribute,
  mockRichComponent,
  mockRichMaterialAttribute,
  mockRichFinishAttribute,
  // Composite product info
  mockCompositeProductInfo,
} from '../mockData';
import styles from './StyleGuideContainers.module.css';
import optionStyles from './StyleGuideOptions.module.css';

export const StyleGuideContainers: React.FC = observer(function StyleGuideContainers() {
  // ============================================================================
  // Basic Container Children (simple demos)
  // ============================================================================

  // Simple children for basic container demos
  const simpleChildren = useMemo(
    () => [
      createMockTextAttributeChild(mockColorAttributeConfig),
      createMockTextAttributeChild(mockSizeAttributeConfig),
    ],
    [],
  );

  // Component + attributes for ComponentItems demo
  const componentItemsChildren = useMemo(
    () => [
      createMockComponentChild(mockCoffeeMachineComponent),
      createMockTextAttributeChild(mockColorAttributeConfig),
      createMockTextAttributeChild(mockSizeAttributeConfig),
    ],
    [],
  );

  // ============================================================================
  // Rich Container Children (comprehensive demos with titles + descriptions)
  // ============================================================================

  // Rich children for Container Title Variations - showing attribute title/description visibility
  const richTitleDemoChildren = useMemo(
    () => [createMockTextAttributeChild(mockRichColorAttribute), createMockTextAttributeChild(mockRichSizeAttribute)],
    [],
  );

  // Rich children for Container State Variations - component + attributes
  const richStateDemoChildren = useMemo(
    () => [
      createMockComponentChild(mockRichComponent),
      createMockTextAttributeChild(mockRichColorAttribute),
      createMockBooleanAttributeChild(mockRichBooleanAttribute),
    ],
    [],
  );

  // Rich children for collapsed container demo
  const richCollapsedChildren = useMemo(
    () => [
      createMockTextAttributeChild(mockRichMaterialAttribute),
      createMockIntegerAttributeChild(mockRichIntegerAttribute),
    ],
    [],
  );

  // Rich children for non-expandable container demo
  const richNonExpandableChildren = useMemo(
    () => [
      createMockTextAttributeChild(mockRichFinishAttribute),
      createMockBooleanAttributeChild(mockRichBooleanAttribute),
    ],
    [],
  );

  // ============================================================================
  // Nested Container Children (4 levels deep with rich content)
  // ============================================================================

  // Level 4: Component Items Container with component + attributes
  const nestedComponentItemsChildren = useMemo(
    () => [
      createMockComponentChild(mockRichComponent),
      createMockTextAttributeChild(mockRichColorAttribute),
      createMockBooleanAttributeChild(mockRichBooleanAttribute),
    ],
    [],
  );

  // Level 3: Mixed Container (collapsed) containing Component Items + attribute
  const level3DirectContent = useMemo(() => [createMockTextAttributeChild(mockRichMaterialAttribute)], []);

  const nestedLevel3Children = useMemo(
    () => [
      ...level3DirectContent,
      createMockContainerChild(
        ComponentItemsContainerCfgUIElement,
        { ...mockNestedComponentItems, expandable: false, defaultExpanded: true },
        nestedComponentItemsChildren,
      ),
    ],
    [level3DirectContent, nestedComponentItemsChildren],
  );

  // Level 2: Mixed Container (expanded) containing attribute + Level 3
  const level2DirectContent = useMemo(() => [createMockTextAttributeChild(mockRichSizeAttribute)], []);

  const nestedLevel2Children = useMemo(
    () => [
      ...level2DirectContent,
      createMockContainerChild(
        MixedContainerCfgUIElement,
        { ...mockNestedMixedLevel3, expandable: false, defaultExpanded: true },
        nestedLevel3Children,
      ),
    ],
    [level2DirectContent, nestedLevel3Children],
  );

  // Additional Level 2 container with attributes for variety
  const additionalLevel2Children = useMemo(() => [createMockTextAttributeChild(mockRichFinishAttribute)], []);

  // Composite product info for the nested containers demo
  const compositeProductInfoState = useMemo(() => new MockCompositeProductInfoState(mockCompositeProductInfo), []);

  // Full nested structure for the demo (Root -> Mixed -> Mixed -> ComponentItems)
  const nestedRootChildren = useMemo(
    () => [
      createMockContainerChild(
        MixedContainerCfgUIElement,
        { ...mockNestedMixedLevel2, expandable: false, defaultExpanded: true },
        nestedLevel2Children,
      ),
      createMockContainerChild(
        MixedContainerCfgUIElement,
        {
          ...mockContainerExpandedByDefault,
          id: 'additional-level-2',
          title: 'CNT_T: Additional Options',
          description: 'CNT_D: More configuration options at root level',
          expandable: false,
          defaultExpanded: true,
        },
        additionalLevel2Children,
      ),
    ],
    [nestedLevel2Children, additionalLevel2Children],
  );

  return (
    <div className={styles.section}>
      {/* Root Container */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Root Container</h2>
        <p className={styles.containerDescription}>
          Top-level wrapper that holds all configurator elements. No visual styling, just layout.
        </p>
        <div className={styles.demo}>
          <RootContainerCfgUIElement viewElement={mockRootContainer} childrenItems={simpleChildren} />
        </div>
      </div>

      {/* Mixed Container - Normal */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Mixed Container (Normal)</h2>
        <p className={styles.containerDescription}>
          Flexible container with optional title, description, and expand/collapse. Vertical layout.
        </p>
        <div className={styles.demo}>
          <MixedContainerCfgUIElement
            viewElement={{
              ...mockMixedContainer,
              title: 'Configuration Options',
              expandable: true,
              defaultExpanded: true,
            }}
            childrenItems={simpleChildren}
          />
        </div>
      </div>

      {/* Mixed Container - Grid */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Mixed Container (Grid Mode)</h2>
        <p className={styles.containerDescription}>
          Mixed container with combineIntoGrid: true. Arranges children in a grid layout.
        </p>
        <div className={styles.demo}>
          <MixedContainerCfgUIElement viewElement={mockMixedContainerGrid} childrenItems={simpleChildren} />
        </div>
      </div>

      {/* Component Items Container */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Component Items Container</h2>
        <p className={styles.containerDescription}>
          Groups a component with its related attributes. First child is component, rest are attributes.
        </p>
        <div className={styles.demo}>
          <ComponentItemsContainerCfgUIElement
            viewElement={mockComponentItemsContainer}
            childrenItems={componentItemsChildren}
          />
        </div>
      </div>

      {/* Products Container */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Products Container</h2>
        <p className={styles.containerDescription}>
          Displays product variants from components. Can show as cards or list.
        </p>
        <div className={styles.demo}>
          <ProductsContainerCfgUIElement viewElement={mockProductMergeContainer} childrenItems={[]} />
        </div>
      </div>

      {/* ================================================================== */}
      {/* SECTION: CONTAINER TITLE VARIATIONS */}
      {/* ================================================================== */}
      <div className={optionStyles.sectionHeader}>
        <h2 className={optionStyles.sectionTitle}>Container Title Variations</h2>
        <p className={optionStyles.sectionDescription}>
          Demonstrates showTitle and showDescription settings on containers. Each container includes attributes with
          visible titles and descriptions.
        </p>
      </div>

      {/* Container: Title + Description */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Container: Title + Description</h2>
        <p className={styles.containerDescription}>
          Both title and description are visible. Full header with expand/collapse. Contains: Color attribute (grid
          layout with images) + Size attribute (list layout).
        </p>
        <div className={styles.demo}>
          <MixedContainerCfgUIElement viewElement={mockContainerTitleDesc} childrenItems={richTitleDemoChildren} />
        </div>
      </div>

      {/* Container: Title only */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Container: Title Only</h2>
        <p className={styles.containerDescription}>
          Only title is visible. Description is hidden (showDescription: false). Contains: Color attribute (grid layout
          with images) + Size attribute (list layout).
        </p>
        <div className={styles.demo}>
          <MixedContainerCfgUIElement viewElement={mockContainerTitleOnly} childrenItems={richTitleDemoChildren} />
        </div>
      </div>

      {/* Container: No title (content only) */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Container: No Title (Content Only)</h2>
        <p className={styles.containerDescription}>
          No title or description visible (showTitle: false, showDescription: false). Container renders only its content
          with expand button. Contains: Color attribute (grid layout with images) + Size attribute (list layout).
        </p>
        <div className={styles.demo}>
          <MixedContainerCfgUIElement viewElement={mockContainerNoTitle} childrenItems={richTitleDemoChildren} />
        </div>
      </div>

      {/* ================================================================== */}
      {/* SECTION: CONTAINER STATE VARIATIONS */}
      {/* ================================================================== */}
      <div className={optionStyles.sectionHeader}>
        <h2 className={optionStyles.sectionTitle}>Container State Variations</h2>
        <p className={optionStyles.sectionDescription}>
          Demonstrates expandable and defaultExpanded settings on containers. Each container includes components and
          attributes to show expand/collapse behavior.
        </p>
      </div>

      {/* Container: Expanded by default */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Container: Expanded by Default</h2>
        <p className={styles.containerDescription}>
          Container starts expanded (defaultExpanded: true). User can collapse it. Contains: Component switch + Color
          attribute (grid) + Boolean toggle.
        </p>
        <div className={styles.demo}>
          <MixedContainerCfgUIElement
            viewElement={mockContainerExpandedByDefault}
            childrenItems={richStateDemoChildren}
          />
        </div>
      </div>

      {/* Container: Collapsed by default */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Container: Collapsed by Default</h2>
        <p className={styles.containerDescription}>
          Container starts collapsed (defaultExpanded: false). User can expand it. Contains: Material attribute (grid) +
          Quantity selector. Click to expand and see content.
        </p>
        <div className={styles.demo}>
          <MixedContainerCfgUIElement
            viewElement={mockContainerCollapsedByDefault}
            childrenItems={richCollapsedChildren}
          />
        </div>
      </div>

      {/* Container: Non-expandable */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Container: Non-Expandable (Always Visible)</h2>
        <p className={styles.containerDescription}>
          Container cannot be collapsed (expandable: false). No expand button shown. Contains: Surface finish attribute
          (list) + Boolean toggle.
        </p>
        <div className={styles.demo}>
          <MixedContainerCfgUIElement
            viewElement={mockContainerNonExpandable}
            childrenItems={richNonExpandableChildren}
          />
        </div>
      </div>

      {/* ================================================================== */}
      {/* SECTION: NESTED CONTAINERS */}
      {/* ================================================================== */}
      <div className={optionStyles.sectionHeader}>
        <h2 className={optionStyles.sectionTitle}>Nested Containers (4 Levels)</h2>
        <p className={optionStyles.sectionDescription}>
          Demonstrates deep container nesting with meaningful content at each level. Each container has its own
          attributes, and the deepest level contains a Component Items container.
        </p>
      </div>

      {/* Nested Containers Demo */}
      <div className={styles.containerDemo}>
        <h2 className={styles.containerTitle}>Root - Mixed - Mixed - Component Items (All Non-Collapsible)</h2>
        <p className={styles.containerDescription}>
          Starts with composite product title and description, followed by a four-level nesting structure with all
          containers always expanded (expandable: false). Root contains two Mixed containers. All content is visible -
          no collapse buttons. This demonstrates deep nesting with full content visibility for design review.
        </p>
        <div className={styles.demo}>
          <ElementRenderer state={compositeProductInfoState as any} />
          <RootContainerCfgUIElement
            viewElement={{ ...mockRootContainer, id: 'nested-root' }}
            childrenItems={nestedRootChildren}
          />
        </div>
        <div className={styles.structureLegend}>
          <h4>Structure:</h4>
          <pre className={styles.structureTree}>{`Composite Product Info (title + description)
Root Container
├── Level 2: Mixed Container (non-collapsible)
│   ├── Size Attribute (list layout)
│   └── Level 3: Mixed Container (non-collapsible)
│       ├── Material Attribute (grid layout)
│       └── Level 4: Component Items Container (non-collapsible)
│           ├── Component Switch (toggle)
│           ├── Color Attribute (grid layout)
│           └── Boolean Attribute (toggle)
└── Additional Options: Mixed Container (non-collapsible)
    └── Surface Finish Attribute (list layout)`}</pre>
        </div>
      </div>
    </div>
  );
});
